/**
 * Status → tone for the whole admin (M23-T03).
 *
 * Data, not a component, so it lives beside `StatusPill` rather than inside it
 * — a shared constant in a component file breaks fast refresh, and this table
 * is imported by more than the pill.
 */

export type StatusTone = 'attention' | 'active' | 'done' | 'muted' | 'bad'

// M17-T14: the amber pill takes text-ink-1, not text-bed-warn. --bed-warn on
// --bed-warn-bg measures 3.76:1, below AA for pill text at this size. The amber
// stays as the status signal on the background; only the label goes dark. There
// is no dark step on the warning ramp to reach for, so when the designer
// supplies one it lands here.
export const TONE_STYLES: Record<StatusTone, string> = {
  attention: 'bg-bed-warn-bg text-ink-1',
  active: 'bg-accent-bg text-accent-ink',
  done: 'bg-bed-good-bg text-bed-good',
  muted: 'bg-bg-sunken text-ink-3',
  bad: 'bg-bed-bad-bg text-bed-bad',
}

/**
 * Status → tone for every vocabulary in the admin. One table, so a status
 * cannot mean two things in two queues.
 *
 * NOTE `active` and `done` are currently indistinguishable — `--accent-ink` and
 * `--bed-good` are the same value, a known finding tripwired in
 * `adminContrast.test.ts`. That is survivable only because **every pill carries
 * its text label**; colour is never the only signal (CLAUDE.md). Do not "fix"
 * it by inventing an admin palette — it needs the designer.
 */
const STATUS_TONES: Record<string, StatusTone> = {
  // waiting on us
  new: 'attention',
  requested: 'attention',
  pending: 'attention',
  pending_payment: 'attention',
  awaiting_pick: 'attention',
  // in flight
  in_progress: 'active',
  confirmed: 'active',
  held: 'active',
  offered: 'active',
  secured: 'active',
  // resolved
  completed: 'done',
  responded: 'done',
  converted: 'done',
  released: 'done',
  accepted: 'done',
  // over
  closed: 'muted',
  cancelled: 'muted',
  declined: 'muted',
  expired: 'muted',
  // failed
  failed: 'bad',
  // emergency coordination (M36-T07, spec §D.2)
  request_received: 'attention',
  acknowledged: 'active',
  contact_attempted: 'active',
  hospital_contacted: 'active',
  awaiting_response: 'active',
  hospital_confirmed: 'done',
  unable_to_coordinate: 'muted',
  // Medcor Transport (M40-T12). `confirmed` and `completed` are above. A paid
  // booking with no driver is in flight, not waiting on us: the queue's red
  // "Past cut-off" rows carry the urgency.
  driver_assigned: 'active',
  picked_up: 'active',
  arrived: 'active',
  return_requested: 'attention',
  return_picked_up: 'active',
  cancelled_by_customer: 'muted',
  cannot_fulfil: 'bad',
  no_show: 'muted',
}

/** The tone for a status, defaulting to `muted` for anything unrecognised. */
export function toneFor(status: string): StatusTone {
  return STATUS_TONES[status] ?? 'muted'
}
